import React from 'react';
import { CatmullRomCurve3, Quaternion, Vector3 } from 'three';

import { formatForce } from '@paragon/client-tools';
import { Point3D, Vector3D } from '@paragon/geometry-library';
import { Segment3D } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getPointFromPolynomial } from '../../library/getPolynomialPoint';
import { SegmentLabel } from '../util/SegmentLabel';

const getValueFormatter = (linkResultType: ResultCurveType): ((value: number) => string) =>
  linkResultType === ResultCurveType.ShearCurve
    ? formatForce
    : linkResultType === ResultCurveType.MomentCurve
      ? (poundInches) => `${poundInches.toFixed(3)} lb-in.`
      : linkResultType === ResultCurveType.DeflectionCurve
        ? (inches) => `${inches.toFixed(3)} in.`
        : assertExhaustiveSwitch(linkResultType);

export enum ResultCurveType {
  ShearCurve = 0,
  MomentCurve,
  DeflectionCurve,
}

function getMaterialColorFromType(linkResultType: ResultCurveType) {
  switch (linkResultType) {
    case ResultCurveType.DeflectionCurve:
      return 0x44ee44;
    case ResultCurveType.MomentCurve:
      return 0xe87209;
    case ResultCurveType.ShearCurve:
      return 0x09e8a8;
    default:
      assertExhaustiveSwitch(linkResultType);
  }
}

interface Props {
  readonly linkSegment: Segment3D;
  readonly coefficients: readonly number[];
  readonly scale: number;
  readonly linkResultType: ResultCurveType;
}

export function ResultCurveVisual({ linkSegment, coefficients, scale, linkResultType }: Props) {
  const start = Point3D.fromObject(linkSegment.start);
  const end = Point3D.fromObject(linkSegment.end);

  const linkVector = Vector3D.fromPointAndPoint(start, end);
  const normalizedLinkDirection = new Vector3().copy(linkVector).normalize();
  const quaternion = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), normalizedLinkDirection);

  const valueCount = 26;
  const valueSpacing = linkVector.magnitude / (valueCount - 1);
  const values: number[] = [];
  for (let i = 0; i < valueCount; i += 1) {
    values[i] = getPointFromPolynomial(i * valueSpacing, coefficients);
  }
  const points = values.map((value, index) => new Vector3(index * valueSpacing, value * scale, 0));
  const path = new CatmullRomCurve3(points);

  const max = Math.max(...values);
  const min = Math.min(...values);
  const greatestMagnitudeValue = max < Math.abs(min) ? min : max;

  const valueFormatter = getValueFormatter(linkResultType);

  const labelQuaternion = new Quaternion().copy(quaternion).invert();

  return (
    <object3D position={[start.x, start.y, start.z + 2]} quaternion={quaternion}>
      <mesh>
        <tubeGeometry args={[path, 10, 0.5, 10, false]} />
        <meshBasicMaterial color={getMaterialColorFromType(linkResultType)} />
      </mesh>
      <SegmentLabel
        segment={{ start: { x: 0, y: 0, z: 0 }, end: linkVector }}
        text={valueFormatter(greatestMagnitudeValue)}
        quaternion={labelQuaternion}
      />
    </object3D>
  );
}
